import { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { KnowledgeSource, RejectionReason } from '../../types';

/**
 * F11: why a crawl was refused, and what to do about it.
 *
 * Two states arrive here and they are genuinely different, so the panel says which
 * one it is in its first line rather than leaving the customer to work it out:
 *
 *   status 'rejected'                    the FIRST crawl was refused. Nothing of
 *                                        this site is in the chatbot and nothing
 *                                        ever was — it was invisible from the
 *                                        moment it was committed to the verdict,
 *                                        so no answer ever used it.
 *   status 'indexed' + rejectionReason   a REFRESH was refused. The new copy was
 *                                        thrown away and the earlier one is still
 *                                        live. Nothing was lost.
 *
 * A slide-over rather than an inline expansion, matching CrawledPagesPanel: the
 * breakdown runs to eight rows plus the advice, and the list must not be pushed off
 * screen by the thing explaining one of its rows.
 */

const HEADLINE: Record<RejectionReason, string> = {
  over_storage_cap: 'This website did not fit in your storage',
  too_many_pages: 'This website was too large to read',
  took_too_long: 'This website took too long to read',
};

/**
 * What actually fixes it. Split by reason because the fixes are not the same: the
 * cap is about the ACCOUNT and is fixed by freeing space anywhere, while both rails
 * are about this ONE site and are fixed by narrowing it.
 */
const WHAT_TO_DO: Record<RejectionReason, string> = {
  over_storage_cap:
    'Free up space by removing sources you no longer need — from this chatbot or any '
    + 'other, since the allowance is shared across your account — or add the site again '
    + 'covering less of it.',
  too_many_pages:
    'Add the site again pointing at a section rather than the whole domain, or '
    + 'excluding the parts your chatbot does not need to answer about.',
  took_too_long:
    'This usually means the site is very large or very slow to serve pages. Add it '
    + 'again pointing at a smaller section of it.',
};

/**
 * The cap advice on a refused REFRESH has to end somewhere different (F13).
 *
 * "Add the site again" is the right answer for a first crawl and the wrong one here:
 * the row is live and indexed, so `add_website` blocks the duplicate URL — correctly,
 * because two live copies of one site is duplicate content. The action that exists is
 * the refresh button on the row, which used to be disabled at exactly this moment and
 * is what made the advice a dead end rather than merely imprecise.
 *
 * Only the cap case differs. A rail is about how the site is built, so freeing space
 * changes nothing and re-reading it unchanged reaches the same verdict — those keep
 * pointing at narrowing the crawl.
 */
const WHAT_TO_DO_ON_REFRESH: Partial<Record<RejectionReason, string>> = {
  over_storage_cap:
    'Free up space by removing sources you no longer need — from this chatbot or any '
    + 'other, since the allowance is shared across your account — then read this site '
    + 'again with the refresh button on its row.',
};

export default function RejectionPanel({
  source,
  onClose,
}: {
  source: KnowledgeSource;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const site = source.website;
  const reason = site?.rejectionReason;
  const breakdown = site?.rejectionBreakdown;
  const wasRefresh = source.status !== 'rejected';

  // The pages the table does NOT account for (C14). Clamped at zero: `totalPages`
  // and the per-prefix counts come from the same summarise pass so it should never
  // go negative, but a negative row would be a nonsense the customer has to explain
  // to themselves, and hiding it costs nothing.
  const shownPages = (breakdown?.prefixes ?? []).reduce((sum, e) => sum + e.pages, 0);
  const remainderPages = Math.max(0, (breakdown?.totalPages ?? 0) - shownPages);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-gray-900/30" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label={`Why ${source.name} was not added`}
        className="relative flex h-full w-full max-w-xl flex-col bg-white dark:bg-slate-800 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {reason ? HEADLINE[reason] : 'This website was not added'}
            </h3>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={source.detail}>
              {source.name}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* What happened to their content. The single most reassuring fact in
              either case, and the one they cannot see for themselves. */}
          <div className="rounded-xl bg-gray-50 dark:bg-slate-700/40 px-4 py-3">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {wasRefresh
                ? 'The earlier version of this website is still in use. Your chatbot kept '
                  + 'answering from it throughout, and nothing was lost.'
                : 'Nothing was added to your chatbot, and nothing was removed. This website '
                  + 'was never used to answer anything.'}
            </p>
          </div>

          {source.errorMessage && (
            <p className="mt-4 text-sm text-gray-700 dark:text-gray-200">{source.errorMessage}</p>
          )}

          {reason && (
            <>
              <h4 className="mt-6 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                What you can do
              </h4>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                {(wasRefresh && WHAT_TO_DO_ON_REFRESH[reason]) || WHAT_TO_DO[reason]}
              </p>
            </>
          )}

          {/* The breakdown. Present only for a first-crawl rejection, and not always
              even then — see the absent case below. */}
          {breakdown && breakdown.prefixes.length > 0 && (
            <>
              <h4 className="mt-6 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Where the pages were
              </h4>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {/* "Approximate" is not hedging. These are page counts, and pages vary
                    more than 11x in weight across real sites — so the biggest section
                    by count is not reliably the biggest by size. Saying so is what
                    stops the numbers being read as a storage breakdown. */}
                {breakdown.totalPages.toLocaleString()} pages were read, grouped by the first
                part of their address. Page counts are a rough guide to size, not a
                measurement — some pages are far heavier than others.
              </p>

              <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700">
                <table className="w-full text-sm">
                  <tbody>
                    {breakdown.prefixes.map(entry => (
                      <tr
                        key={entry.prefix}
                        className="border-b border-gray-100 last:border-b-0 dark:border-slate-700/60"
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                          {entry.prefix}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                          {entry.pages.toLocaleString()} {entry.pages === 1 ? 'page' : 'pages'}
                        </td>
                        <td className="w-16 px-4 py-2.5 text-right tabular-nums text-gray-400 dark:text-gray-500">
                          {Math.round(entry.sharePercent)}%
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {entry.suggestedExclusion && (
                            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                              {entry.suggestedExclusion}
                            </code>
                          )}
                        </td>
                      </tr>
                    ))}

                    {/* "Everything else" (C14). `rails.summarise_urls` drops any
                        prefix below MIN_PREFIX_PAGES = 2 — sound, since a one-page
                        section is not worth suggesting an exclusion for and only
                        clutters the advice — but the table was then presented as
                        WHERE THE PAGES WERE, next to a total counting all of them.
                        The first real rejection rendered said "10 pages were read"
                        above a table accounting for 2.

                        Worst exactly where the feature is needed: the sites that get
                        rejected are large, and a large flat site is precisely one
                        where most prefixes hold a single page.

                        So the filter stays and the total stops lying. This is also
                        the more useful of the two fixes considered — rewording the
                        heading to "sections with 2 or more pages" would have been
                        honest but silent about the spread, and a site spread thin is
                        itself the actionable fact. */}
                    {remainderPages > 0 && (
                      <tr className="border-b border-gray-100 last:border-b-0 dark:border-slate-700/60">
                        <td className="px-4 py-2.5 italic text-gray-500 dark:text-gray-400">
                          everything else
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                          {remainderPages.toLocaleString()}{' '}
                          {remainderPages === 1 ? 'page' : 'pages'}
                        </td>
                        <td className="w-16 px-4 py-2.5 text-right tabular-nums text-gray-400 dark:text-gray-500">
                          {Math.round((remainderPages / breakdown.totalPages) * 100)}%
                        </td>
                        {/* Deliberately no suggested exclusion. These pages are
                            scattered across sections too small to name, so there is
                            no pattern to offer — inventing one would exclude more
                            than it should. */}
                        <td className="px-4 py-2.5" />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                To leave a section out, add the website again and put its pattern in
                Crawl options → Exclude.
              </p>
            </>
          )}

          {/* ABSENT IS NOT EMPTY. The page records are delivered minutes after the
              crawl and the verdict lands inside that window, so a perfectly ordinary
              rejection can arrive with no breakdown. Rendering that as an empty
              table would say "this crawl read nothing", which is the opposite of
              what happened and indistinguishable from it in the data. */}
          {!breakdown && (
            <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
              We could not list which parts of the site were read — those records arrive a
              few minutes after a crawl and this one was stopped before they did. That is
              not a sign the site was empty.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
